import type { ResourceType, ResourceTypeHandle, Tool, ToolHandle } from '@toolproof-core/genesis/_contract/_index';

import { extractProjectionMapByHandle } from '@toolproof-core/lib/_pure/cosmos-data-extraction';
import {
    getBranchStepWhatSlotEligibleToolHandles,
    getFilteredToolMap,
    getLoopStepWhatSlotEligibleToolHandles,
    getMacroStepWhenSlotEligibleToolHandles,
} from '@toolproof-core/lib/_pure/tool-filters';
import { extractResourceTypeHandleFromRole } from '@toolproof-core/lib/_pure/workflow-extraction';
import type { ResourcesByType, TimestampByHandleByType } from '@toolproof-core/lib/_contract/resources';

import { TYPE_ResourceType, TYPE_Tool, TYPE_WorkflowTrace } from '@/workflow/_data/cosmosData';
import type { ResourceTypeRequirement } from '@/workflow/_contract/config';
import type { CosmosDataModel } from '@/workflow/_contract/cosmosData';

export function getResourceTypesUsedByTools(toolMap: Map<ToolHandle, Tool>): Set<ResourceTypeHandle> {
    const usedTypes = new Set<ResourceTypeHandle>();

    for (const tool of toolMap.values()) {
        for (const role of Object.values(tool.roleSpec.inputRoleByName)) {
            const typeHandle = extractResourceTypeHandleFromRole(role);
            if (typeHandle) usedTypes.add(typeHandle);
        }

        for (const role of Object.values(tool.roleSpec.outputRoleByName)) {
            const typeHandle = extractResourceTypeHandleFromRole(role);
            if (typeHandle) usedTypes.add(typeHandle);
        }
    }

    return usedTypes;
}

function buildCosmosDataFromResourcesByType(
    resourcesByType: ResourcesByType<ResourceTypeRequirement>,
    timestampByHandleByType: TimestampByHandleByType
): CosmosDataModel {
    const toolMap = getFilteredToolMap(
        extractProjectionMapByHandle<ToolHandle, Tool>(resourcesByType[TYPE_Tool])
    );

    const usedResourceTypeHandles = getResourceTypesUsedByTools(toolMap);
    const allResourceTypes = resourcesByType[TYPE_ResourceType] ?? [];
    const usedResourceTypes = allResourceTypes.filter((rt) => {
        const projection = rt.projection as { handle?: ResourceTypeHandle } | null;
        const handle = projection?.handle;
        return Boolean(handle && usedResourceTypeHandles.has(handle));
    });

    const resourceTypeMap = extractProjectionMapByHandle<ResourceTypeHandle, ResourceType>(
        usedResourceTypes
    );

    const loopStepWhatSlotEligibleToolHandles = getLoopStepWhatSlotEligibleToolHandles(toolMap);
    const branchStepWhatSlotEligibleToolHandles = getBranchStepWhatSlotEligibleToolHandles(toolMap);
    const macroStepWhenSlotEligibleToolHandles = getMacroStepWhenSlotEligibleToolHandles(toolMap);

    return {
        resourcesByType,
        timestampByHandleByType,
        resourceTypeMap,
        toolMap,
        loopStepWhatSlotEligibleToolHandles,
        branchStepWhatSlotEligibleToolHandles,
        macroStepWhenSlotEligibleToolHandles,
    };
}

export function buildCosmosData(
    resourcesByType: ResourcesByType<ResourceTypeRequirement>,
    timestampByHandleByType?: TimestampByHandleByType
): CosmosDataModel {
    const filteredTimestampByHandleByType: TimestampByHandleByType = {};
    const traceTimestamps = timestampByHandleByType?.[TYPE_WorkflowTrace];
    if (traceTimestamps) {
        filteredTimestampByHandleByType[TYPE_WorkflowTrace] = traceTimestamps;
    }

    return buildCosmosDataFromResourcesByType(
        resourcesByType,
        filteredTimestampByHandleByType
    );
}
